/*
This script continuously loads posts as the user scrolls the page. It uses the IntersectionObserver API to detect when the last post is in view. When the last post is in view, it fetches the next page and appends the posts to the page. It then checks if there is a next page and if there is, it continues to observe the last post. If there is no next page, it disconnects the observer.

Importantly, for this script to work, it requires that each card have the `post` class and that the card container have the `gh-postfeed` class.

When the feed opts into calendar-week grouping (`data-kw-group`), fetched posts are
handed to `distribute()` so they join/extend the existing week <section> wrappers
instead of being appended as flat siblings.
*/

import { distribute } from './weekGroups';

let link = document.querySelector('link[rel="next"]')?.getAttribute('href');

// The feed container. Prefer a calendar-week feed, fall back to a plain feed.
const feed =
    document.querySelector('.gh-postfeed[data-kw-group]') ||
    document.querySelector('.gh-postfeed');

// The last `.post` in DOM order, wherever it now lives (flat feed or nested in a week group).
function getLastPost() {
    if (!feed) { return null; }
    const posts = feed.querySelectorAll('.post');
    return posts.length ? posts[posts.length - 1] : null;
}

// Append fetched cards, grouping them when the feed opts in.
function appendPosts(posts) {
    const cards = Array.from(posts);
    if (feed.hasAttribute('data-kw-group')) {
        distribute(feed, cards);
    } else {
        cards.forEach(post => feed.append(post));
    }
}

// Fetch and parse next page
async function getNextPage(url) {
    try {
        const res = await fetch(url);

        if (!res.ok) {
            throw new Error('Failed to fetch page')
        }

        const nextPageHtml = await res.text();
        const parser = new DOMParser();
        const parsed = parser.parseFromString(nextPageHtml, 'text/html');
        const posts = parsed.querySelectorAll('.post');
        const nextLink = parsed.querySelector('link[rel="next"]')?.getAttribute('href');

        return {posts, nextLink}

    } catch (error) {
        throw new Error(error)
    }
}

export default function infiniteScroll() {

    if (!link || !feed) { return; }

    const options = {
        // When the last card is within a 150px of the viewport, fetch the next page. This provides a smoother transition between pages
       rootMargin: '150px',
    }

    const callback = (entries, observer) => {
        try {
            entries.forEach(entry => {

                if (entry.isIntersecting) {

                    if (link) {
                        getNextPage(link).then(({posts, nextLink}) => {
                            appendPosts(posts);

                            if (nextLink) {
                                link = nextLink;
                                const lastPost = getLastPost();
                                if (lastPost) {
                                    observer.observe(lastPost);
                                }
                            } else {
                                observer.disconnect()
                            }
                        })
                    }
                }
            })
        } catch (error) {
            console.log(error)
        }
    }

    let observer = new IntersectionObserver(callback, options);

    const lastPost = getLastPost();
    if (lastPost) {
        observer.observe(lastPost);
    }

}
